import {
  CANVAS_ELEMENT_TYPES,
  CANVAS_TOOLS,
  createCanvasElementId,
  createElementForTool,
  isBoxElement,
  updateElementText,
  updateRectangleLabel,
} from "../nativeCanvasElements";
import { DEFAULT_ELEMENT_STYLE } from "../canvasDefaults";

const TEXT_KEY_FOR_BOX = "label";
const TEXT_KEY_FOR_TEXT = "text";

const getEditableTextKey = (element) => {
  if (isBoxElement(element)) return TEXT_KEY_FOR_BOX;
  if (element.type === CANVAS_ELEMENT_TYPES.TEXT) return TEXT_KEY_FOR_TEXT;

  return null;
};

/**
 * Label/text editing on canvas elements: double-click a shape to edit its
 * label, double-click empty canvas (or click with the text tool) to type a
 * new text element. New text stays pending — outside the scene and history —
 * until committed with content, so abandoned editors leave nothing behind.
 * @param {{ commitElements: Function, notifyUi: Function, requestRender: Function, state: object }} params
 * @returns {object}
 */
export const createTextEditingActions = ({
  commitElements,
  notifyUi,
  requestRender,
  state,
}) => {
  const refreshEditingVisuals = () => {
    notifyUi();
    requestRender("both");
  };

  const getEditingElement = () => {
    const editing = state.editingText;
    if (!editing) return null;

    return (
      editing.pendingElement ??
      state.elements.find((element) => element.id === editing.elementId) ??
      null
    );
  };

  const startTextEditing = (elementId) => {
    const element = state.elements.find((candidate) => candidate.id === elementId);
    const textKey = element ? getEditableTextKey(element) : null;
    if (!textKey) return;

    state.editingText = { elementId, textKey, value: element[textKey] ?? "" };
    state.selectedIds = new Set([elementId]);
    refreshEditingVisuals();
  };

  const commitPendingElement = (editing) => {
    if (!editing.value.trim()) return refreshEditingVisuals();

    commitElements(
      [...state.elements, { ...editing.pendingElement, text: editing.value }],
      { selectIds: new Set([editing.pendingElement.id]) }
    );
  };

  const commitExistingElement = (editing) => {
    const element = state.elements.find(
      (candidate) => candidate.id === editing.elementId
    );
    if (!element || (element[editing.textKey] ?? "") === editing.value) {
      return refreshEditingVisuals();
    }
    if (editing.textKey === TEXT_KEY_FOR_TEXT && !editing.value.trim()) {
      return commitElements(
        state.elements.filter((candidate) => candidate.id !== element.id),
        { selectIds: new Set() }
      );
    }
    const updatedElement =
      editing.textKey === TEXT_KEY_FOR_BOX
        ? updateRectangleLabel({ element, label: editing.value })
        : updateElementText({ element, text: editing.value });
    commitElements(
      state.elements.map((candidate) =>
        candidate.id === element.id ? updatedElement : candidate
      )
    );
  };

  const commitTextEditing = () => {
    const editing = state.editingText;
    if (!editing) return;
    state.editingText = null;
    if (editing.pendingElement) return commitPendingElement(editing);
    commitExistingElement(editing);
  };

  const cancelTextEditing = () => {
    state.editingText = null;
    refreshEditingVisuals();
  };

  const insertTextElementAt = (point) => {
    const pendingElement = {
      ...createElementForTool({
        id: createCanvasElementId(),
        point,
        style: DEFAULT_ELEMENT_STYLE,
        tool: CANVAS_TOOLS.TEXT,
      }),
      text: "",
    };
    state.editingText = {
      elementId: pendingElement.id,
      pendingElement,
      textKey: TEXT_KEY_FOR_TEXT,
      value: "",
    };
    state.selectedIds = new Set();
    refreshEditingVisuals();
  };

  const updateEditingValue = (value) => {
    if (!state.editingText) return;
    state.editingText = { ...state.editingText, value };
    notifyUi();
  };

  return {
    cancelTextEditing,
    commitTextEditing,
    getEditingElement,
    insertTextElementAt,
    startTextEditing,
    updateEditingValue,
  };
};
